import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { components } from '@/api/schema'
import { useAuthenticatedClient } from '@/api/queries'
import { cn, formatNumber, parseUTCDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type Report = components['schemas']['PriceMatchReportData']
type Crawl = components['schemas']['PriceMatchCrawl']
type Manufacturer = components['schemas']['PriceMatchManufacturer']

const REFRESH_POLL_MS = 10_000
const MANUFACTURER_PREVIEW = 20

const SOURCE_NAMES: Record<string, string> = {
  webstaurant: 'Webstaurant',
  katom: 'KaTom',
  chefsdeal: "Chef's Deal",
}

const OUTCOME_LABELS: Record<string, string> = {
  matched_lower: 'Priced 1% under a competitor',
  matched_higher: 'Raised toward a higher competitor',
  within_stability: 'Already at the competitor price',
  capped_at_ceiling: 'Competitor above our normal markup',
  blocked_by_floor: 'Competitor below our minimum margin',
  kept_at_map: 'Held at MAP',
  outside_band: 'Price too far from ours to compare',
  unit_unknown: 'Pack size unclear',
  no_eligible_offer: 'No current competitor price',
}

function sourceName(source: string): string {
  return SOURCE_NAMES[source] ?? source
}

function share(part: number, whole: number): string {
  if (!whole) return '0%'
  const percent = (part / whole) * 100
  return `${percent > 0 && percent < 1 ? '<1' : Math.round(percent)}%`
}

function formatAgo(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - parseUTCDate(value).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

function formatDays(days: number | null | undefined): string {
  if (days == null) return '—'
  if (days < 1) return 'Under a day'
  const rounded = Math.round(days)
  return `${rounded} day${rounded === 1 ? '' : 's'}`
}

function formatShortDate(value: string): string {
  return parseUTCDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function cycleLabel(cycle: string): string {
  const [year, month, rerun] = cycle.split('-')
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1))
  const name = date.toLocaleDateString(undefined, { month: 'long', timeZone: 'UTC' })
  return rerun ? `${name} pass, rerun` : `${name} pass`
}

function usePriceMatching() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['price-matching'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/price-matching')
      if (error) throw error
      return data!
    },
    refetchInterval: (query) => (query.state.data?.refreshing ? REFRESH_POLL_MS : false),
  })
}

export function PriceMatchingPage() {
  const { data, isLoading, error } = usePriceMatching()

  return (
    <div className="mx-auto max-w-7xl space-y-10">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Price matching</h1>
        {data?.computed_at && (
          <p
            className="flex items-center gap-1.5 text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            {data.refreshing && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            {data.refreshing ? 'Updating · ' : ''}
            As of {formatAgo(data.computed_at)}
          </p>
        )}
      </div>

      {isLoading ? (
        <PageSkeleton />
      ) : error || !data ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          Price matching couldn’t load. Refresh the page to try again.
        </p>
      ) : !data.report ? (
        <div className="space-y-3" role="status" aria-live="polite">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Counting the catalog. This takes about a minute.
          </p>
          <PageSkeleton />
        </div>
      ) : (
        <>
          <CoverageSection report={data.report} />
          <CrawlsSection crawls={data.report.crawls} />
          <WebstaurantSection report={data.report} />
          <ManufacturersSection report={data.report} />
        </>
      )}
    </div>
  )
}

function Segment({ value, total, className }: { value: number; total: number; className: string }) {
  if (!value || !total) return null
  return <div className={className} style={{ width: `${(value / total) * 100}%` }} />
}

function LegendItem({
  swatch,
  label,
  value,
  total,
}: {
  swatch: string
  label: string
  value: number
  total: number
}) {
  return (
    <li className="flex items-baseline gap-2">
      <span className={cn('inline-block h-2.5 w-2.5 shrink-0 translate-y-px rounded-sm', swatch)} />
      <span>{label}</span>
      <span className="tabular-nums font-medium">{formatNumber(value, 0)}</span>
      <span className="tabular-nums text-muted-foreground">{share(value, total)}</span>
    </li>
  )
}

function ShareBar({ value, total }: { value: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-full min-w-16 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-foreground/70"
          style={{ width: `${total ? (value / total) * 100 : 0}%` }}
        />
      </div>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {share(value, total)}
      </span>
    </div>
  )
}

function CoverageSection({ report }: { report: Report }) {
  const notFollowing = report.priced - report.following
  const none = report.listed - report.priced
  return (
    <section aria-labelledby="coverage-heading" className="space-y-5">
      <div className="space-y-1">
        <h2 id="coverage-heading" className="text-base font-semibold">
          Listed products with a competitor price
        </h2>
        <p className="text-3xl font-semibold tracking-tight tabular-nums">
          {formatNumber(report.priced, 0)}
          <span className="text-lg font-normal text-muted-foreground">
            {' '}
            of {formatNumber(report.listed, 0)} · {share(report.priced, report.listed)}
          </span>
        </p>
      </div>

      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={`${formatNumber(report.following, 0)} follow a competitor price, ${formatNumber(notFollowing, 0)} have one but keep our price, ${formatNumber(none, 0)} have none`}
      >
        <Segment value={report.following} total={report.listed} className="bg-foreground" />
        <Segment value={notFollowing} total={report.listed} className="bg-foreground/35" />
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
        <LegendItem
          swatch="bg-foreground"
          label="Priced from a competitor"
          value={report.following}
          total={report.listed}
        />
        <LegendItem
          swatch="bg-foreground/35"
          label="Competitor price, ours kept"
          value={notFollowing}
          total={report.listed}
        />
        <LegendItem
          swatch="bg-muted border"
          label="No competitor price"
          value={none}
          total={report.listed}
        />
      </ul>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Competitor</TableHead>
              <TableHead className="text-right">Carries</TableHead>
              <TableHead className="text-right">Current price</TableHead>
              <TableHead className="hidden w-[30%] sm:table-cell">Of listed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.competitors.map((competitor) => (
              <TableRow key={competitor.source}>
                <TableCell className="font-medium">{sourceName(competitor.source)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(competitor.carried, 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(competitor.priced, 0)}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <ShareBar value={competitor.priced} total={report.listed} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {report.outcomes.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">What the price did</h3>
            <ul className="divide-y rounded-lg border text-sm">
              {report.outcomes.map((outcome) => (
                <li key={outcome.outcome} className="flex justify-between gap-4 px-3 py-2">
                  <span>{OUTCOME_LABELS[outcome.outcome] ?? outcome.outcome}</span>
                  <span className="tabular-nums">{formatNumber(outcome.products, 0)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}

function CrawlStatus({ crawl }: { crawl: Crawl }) {
  if (crawl.state === 'blocked') {
    return (
      <span className="font-medium text-red-700">
        Blocked
        {crawl.last_page_at ? ` since ${formatShortDate(crawl.last_page_at)}` : ''}
        {crawl.blocked_status ? ` (${crawl.blocked_status})` : ''}
      </span>
    )
  }
  const label: Record<Crawl['state'], string> = {
    crawling: 'Crawling',
    waiting: 'Between batches',
    paused: 'Paused',
    blocked: 'Blocked',
    never: 'Not started',
  }
  return (
    <span className={cn(crawl.state === 'crawling' && 'font-medium')}>{label[crawl.state]}</span>
  )
}

function CrawlPass({ crawl }: { crawl: Crawl }) {
  if (!crawl.cycle) return <span className="text-muted-foreground">—</span>
  return (
    <div>
      <p>{cycleLabel(crawl.cycle)}</p>
      <p className="text-xs text-muted-foreground">
        {crawl.phase === 'discovery' ? 'Finding pages' : 'Revisiting pages'}
        {crawl.cycle_started_at ? ` · since ${formatShortDate(crawl.cycle_started_at)}` : ''}
      </p>
    </div>
  )
}

function CatchUp({ crawl }: { crawl: Crawl }) {
  const stale = crawl.pages_known - crawl.pages_fresh
  if (!stale) return <span className="text-muted-foreground">Up to date</span>
  return (
    <div>
      <p className="tabular-nums">{formatDays(crawl.catch_up_days)}</p>
      <p className="text-xs tabular-nums text-muted-foreground">
        {formatNumber(stale, 0)} older pages
      </p>
    </div>
  )
}

function DailyBars({ crawl, max }: { crawl: Crawl; max: number }) {
  const total = crawl.days.reduce((sum, day) => sum + day.pages, 0)
  return (
    <div
      className="flex h-8 items-end gap-0.5"
      role="img"
      aria-label={`${formatNumber(total, 0)} pages in the last ${crawl.days.length} days`}
    >
      {crawl.days.map((day) => (
        <div
          key={day.day}
          className="flex h-full w-1.5 flex-col justify-end"
          title={`${day.day}: ${formatNumber(day.pages, 0)} pages, ${formatNumber(day.matched, 0)} matched`}
        >
          <div
            className={cn('w-full rounded-[1px]', day.pages ? 'bg-foreground/70' : 'bg-muted')}
            style={{ height: day.pages ? `${Math.max(8, (day.pages / max) * 100)}%` : '2px' }}
          />
        </div>
      ))}
    </div>
  )
}

function CrawlsSection({ crawls }: { crawls: Crawl[] }) {
  const max = Math.max(1, ...crawls.flatMap((crawl) => crawl.days.map((day) => day.pages)))
  return (
    <section aria-labelledby="crawls-heading" className="space-y-3">
      <h2 id="crawls-heading" className="text-base font-semibold">
        Crawls
      </h2>

      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Competitor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Monthly pass</TableHead>
              <TableHead className="text-right">Pages</TableHead>
              <TableHead className="text-right">Pages a day</TableHead>
              <TableHead className="text-right">Full pass</TableHead>
              <TableHead>Catch-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {crawls.map((crawl) => (
              <TableRow key={crawl.source}>
                <TableCell className="align-top font-medium">{sourceName(crawl.source)}</TableCell>
                <TableCell className="align-top">
                  <CrawlStatus crawl={crawl} />
                  {crawl.last_page_at && crawl.state !== 'blocked' && (
                    <p className="text-xs text-muted-foreground">
                      Last page {formatAgo(crawl.last_page_at)}
                    </p>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <CrawlPass crawl={crawl} />
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  {formatNumber(crawl.pages_known, 0)}
                  <p className="text-xs text-muted-foreground">
                    {formatNumber(crawl.pages_matched, 0)} ours
                  </p>
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex items-start justify-end gap-3">
                    <DailyBars crawl={crawl} max={max} />
                    <div className="text-right tabular-nums">
                      {formatNumber(crawl.pages_per_day, 0)}
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(crawl.pages_last_24h, 0)} last 24 h
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  {formatDays(crawl.full_pass_days)}
                </TableCell>
                <TableCell className="align-top">
                  <CatchUp crawl={crawl} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="space-y-3 lg:hidden">
        {crawls.map((crawl) => (
          <li key={crawl.source} className="space-y-3 rounded-lg border p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{sourceName(crawl.source)}</p>
                <p className="text-sm">
                  <CrawlStatus crawl={crawl} />
                </p>
              </div>
              <DailyBars crawl={crawl} max={max} />
            </div>
            <CrawlPass crawl={crawl} />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-xs text-muted-foreground">Pages</dt>
                <dd className="tabular-nums">{formatNumber(crawl.pages_known, 0)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Pages a day</dt>
                <dd className="tabular-nums">{formatNumber(crawl.pages_per_day, 0)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Full pass</dt>
                <dd className="tabular-nums">{formatDays(crawl.full_pass_days)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Catch-up</dt>
                <dd>
                  <CatchUp crawl={crawl} />
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </section>
  )
}

function WebstaurantSection({ report }: { report: Report }) {
  const site = report.webstaurant
  const carried = report.competitors.find((competitor) => competitor.source === 'webstaurant')
  const segments = [
    { key: 'ours', label: 'Ours', value: site.our_products, swatch: 'bg-foreground' },
    {
      key: 'our-brands',
      label: 'Our brands, items we don’t list',
      value: site.our_brands_not_listed,
      swatch: 'bg-foreground/55',
    },
    ...(site.brands_known
      ? [
          {
            key: 'own',
            label: 'Webstaurant’s own brands',
            value: site.own_brands,
            swatch: 'bg-foreground/35',
          },
          {
            key: 'only',
            label: 'Other brands sold only at Webstaurant',
            value: site.only_webstaurant,
            swatch: 'bg-foreground/20',
          },
          {
            key: 'other',
            label: 'Other brands we don’t carry',
            value: site.other_brands,
            swatch: 'bg-foreground/10',
          },
        ]
      : [
          {
            key: 'other',
            label: 'Brands we don’t carry',
            value: site.own_brands + (site.only_webstaurant) + site.other_brands,
            swatch: 'bg-foreground/20',
          },
        ]),
  ]
  return (
    <section aria-labelledby="webstaurant-heading" className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="webstaurant-heading" className="text-base font-semibold">
          Webstaurant’s catalog
        </h2>
        <p className="text-sm tabular-nums text-muted-foreground">
          {formatNumber(site.pages, 0)} product pages
          {carried ? ` · carries ${formatNumber(carried.carried, 0)} of our listed products` : ''}
        </p>
      </div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${formatNumber(s.value, 0)}`).join(', ')}
      >
        {segments.map((segment) => (
          <Segment
            key={segment.key}
            value={segment.value}
            total={site.pages}
            className={segment.swatch}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
        {segments.map((segment) => (
          <LegendItem
            key={segment.key}
            swatch={segment.swatch}
            label={segment.label}
            value={segment.value}
            total={site.pages}
          />
        ))}
      </ul>

      {site.brands_known ? (
        site.top_brands.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">
              Top brands we don’t carry
              <span className="ml-2 font-normal text-muted-foreground">
                {formatNumber(site.own_brand_count, 0)} Webstaurant brands
              </span>
            </h3>
            <ul className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {site.top_brands.map((brand) => (
                <li
                  key={brand.brand}
                  className="flex items-baseline justify-between gap-3 border-b border-dashed py-1"
                >
                  <span className="min-w-0 truncate">
                    {brand.brand}
                    {(brand.house || brand.only_webstaurant) && (
                      <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {brand.house ? 'Webstaurant brand' : 'Webstaurant only'}
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatNumber(brand.pages, 0)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )
      ) : (
        <p className="text-sm text-muted-foreground">
          Brand names aren’t kept in this environment’s copy of the crawl.
        </p>
      )}
    </section>
  )
}

function ManufacturerName({ manufacturer }: { manufacturer: Manufacturer }) {
  return manufacturer.manufacturer_uuid ? (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: manufacturer.manufacturer_uuid }}
      className="font-medium text-primary hover:underline"
    >
      {manufacturer.name}
    </Link>
  ) : (
    <span className="font-medium">{manufacturer.name}</span>
  )
}

function ManufacturersSection({ report }: { report: Report }) {
  const [expanded, setExpanded] = useState(false)
  const rows = expanded ? report.manufacturers : report.manufacturers.slice(0, MANUFACTURER_PREVIEW)
  return (
    <section aria-labelledby="manufacturers-heading" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="manufacturers-heading" className="text-base font-semibold">
          Without a competitor price, by manufacturer
        </h2>
        <p className="text-sm tabular-nums text-muted-foreground">
          {formatNumber(report.listed - report.priced, 0)} products ·{' '}
          {formatNumber(report.manufacturers_total, 0)} manufacturers
        </p>
      </div>

      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Manufacturer</TableHead>
              <TableHead className="text-right">Listed</TableHead>
              <TableHead className="text-right">Without</TableHead>
              <TableHead className="text-right">Priced from a competitor</TableHead>
              <TableHead className="w-[25%]">With a competitor price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((manufacturer) => (
              <TableRow key={manufacturer.manufacturer_id ?? manufacturer.name}>
                <TableCell>
                  <ManufacturerName manufacturer={manufacturer} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(manufacturer.listed, 0)}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatNumber(manufacturer.listed - manufacturer.priced, 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(manufacturer.following, 0)}
                </TableCell>
                <TableCell>
                  <ShareBar value={manufacturer.priced} total={manufacturer.listed} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="divide-y rounded-lg border lg:hidden">
        {rows.map((manufacturer) => (
          <li key={manufacturer.manufacturer_id ?? manufacturer.name} className="space-y-1.5 p-3">
            <div className="flex items-baseline justify-between gap-3">
              <ManufacturerName manufacturer={manufacturer} />
              <span className="shrink-0 whitespace-nowrap text-sm tabular-nums">
                {formatNumber(manufacturer.listed - manufacturer.priced, 0)} without
              </span>
            </div>
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatNumber(manufacturer.listed, 0)} listed ·{' '}
              {formatNumber(manufacturer.following, 0)} priced from a competitor
            </p>
            <ShareBar value={manufacturer.priced} total={manufacturer.listed} />
          </li>
        ))}
      </ul>

      {report.manufacturers.length > MANUFACTURER_PREVIEW && (
        <button
          type="button"
          className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show fewer' : `Show top ${report.manufacturers.length}`}
        </button>
      )}
    </section>
  )
}

function PageSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-3 w-full" />
      </div>
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
